import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  EnrollmentStatus,
  MyCoursesItem,
} from "@/modules/learningCenter/features/CourseDirectory/types/viewer";
import { getMyCourses } from "@/modules/learningCenter/features/MyCourses/api/getMyCourses";
import { unenrollCourse } from "@/modules/learningCenter/features/CourseDirectory/api/unenrollCourse";
import { reactivateEnrollment } from "@/modules/learningCenter/features/CourseDirectory/api/reactivateEnrollment";

export type MyCoursesTab =
  | "all"
  | "enrolled"
  | "in_progress"
  | "completed"
  | "cancelled";

export const MY_COURSES_TABS: { value: MyCoursesTab; label: string }[] = [
  { value: "all", label: "All" },
  { value: "enrolled", label: "Enrolled" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

// Pure client-side tab filter. The full transcript is fetched once; tabs filter
// in memory so switching tabs is instant.
export function filterByTab(
  items: MyCoursesItem[],
  tab: MyCoursesTab,
): MyCoursesItem[] {
  if (tab === "all") {
    return items;
  }
  return items.filter((item) => item.status === tab);
}

export interface UseMyCoursesResult {
  items: MyCoursesItem[];
  filteredItems: MyCoursesItem[];
  tab: MyCoursesTab;
  setTab: (tab: MyCoursesTab) => void;
  isLoading: boolean;
  cancel: (enrollmentUuid: string) => Promise<void>;
  reenroll: (enrollmentUuid: string) => Promise<void>;
}

export function useMyCourses(): UseMyCoursesResult {
  const [items, setItems] = useState<MyCoursesItem[]>([]);
  const [tab, setTab] = useState<MyCoursesTab>("all");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    getMyCourses(undefined, controller.signal)
      .then((res) => setItems(res))
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setItems([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });
    return () => controller.abort();
  }, []);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const applyStatus = useCallback(
    (enrollmentUuid: string, status: EnrollmentStatus) => {
      setItems((prev) =>
        prev.map((item) =>
          item.enrollmentUuid === enrollmentUuid ? { ...item, status } : item,
        ),
      );
    },
    [],
  );

  const cancel = useCallback(
    async (enrollmentUuid: string) => {
      const snapshot = itemsRef.current;
      applyStatus(enrollmentUuid, "cancelled");
      try {
        const enrollment = await unenrollCourse(enrollmentUuid);
        applyStatus(enrollmentUuid, enrollment.status);
      } catch (err) {
        setItems(snapshot);
        throw err;
      }
    },
    [applyStatus],
  );

  const reenroll = useCallback(
    async (enrollmentUuid: string) => {
      const snapshot = itemsRef.current;
      applyStatus(enrollmentUuid, "enrolled");
      try {
        const enrollment = await reactivateEnrollment(enrollmentUuid);
        applyStatus(enrollmentUuid, enrollment.status);
      } catch (err) {
        setItems(snapshot);
        throw err;
      }
    },
    [applyStatus],
  );

  return {
    items,
    filteredItems: filterByTab(items, tab),
    tab,
    setTab,
    isLoading,
    cancel,
    reenroll,
  };
}
